export function AvailableTools() {
    return (
        <section id="tools" className="bg-[#363A42]/30 py-12 md:py-20" aria-labelledby="tools-heading">
            <div className="mx-auto mb-12 max-w-6xl px-4 text-center sm:px-6 lg:px-8">
                <h2 id="tools-heading" className="mb-4 text-3xl font-bold text-[#F8FAFC] md:text-4xl">
                    Available Tools
                </h2>
                <p className="mx-auto max-w-2xl text-lg text-[#CBD5E1]">Complete suite of Turkish Airlines integration tools</p>
            </div>

            <div className="mx-auto max-w-6xl space-y-8 px-4 py-12 sm:px-6 md:py-16 lg:px-8">
                {/* Flight Search & Booking */}
                <ToolCategory
                    title="Flight Search & Booking"
                    icon={<FlightIcon />}
                    iconBg="bg-[#E81932]/10"
                    iconColor="text-[#E81932]"
                    tools={[
                        {
                            title: 'Search Flights',
                            description: 'Search available flights by origin, destination, dates, and passenger details. Supports one-way, round trip, and multi-city.'
                        },
                        {
                            title: 'Search Inbound Flights',
                            description: 'Find return flight options after selecting an outbound flight for round-trip bookings'
                        },
                        {
                            title: 'Check Flight Status by Number',
                            description: 'Get real-time flight status, gate, terminal, and delay information by flight number'
                        },
                        {
                            title: 'Check Flight Status by Route',
                            description: 'View all scheduled flights between two airports on a given date with live status'
                        }
                    ]}
                />

                {/* Booking Management */}
                <ToolCategory
                    title="Booking Management"
                    icon={<BookingIcon />}
                    iconBg="bg-[#60A5FA]/10"
                    iconColor="text-[#60A5FA]"
                    tools={[
                        {
                            title: 'Get Booking Details',
                            description: 'Retrieve flight booking details, passenger info, and flight segments using PNR or e-ticket number'
                        },
                        {
                            title: 'Get Booking Baggage Allowance',
                            description: 'Access detailed baggage allowance and extra baggage information for your booking'
                        }
                    ]}
                />

                {/* Travel & Promotions */}
                <ToolCategory
                    title="Travel & Promotions"
                    icon={<TravelIcon />}
                    iconBg="bg-[#22C55E]/10"
                    iconColor="text-[#22C55E]"
                    tools={[
                        {
                            title: 'Get Airline Promotions',
                            description: 'Discover current promotions and special offers by country and airports'
                        },
                        {
                            title: 'Get City Guide',
                            description: 'Access travel guides with tips, attractions, and local information for destination cities'
                        }
                    ]}
                />
            </div>
        </section>
    );
}

interface ToolCategoryProps {
    title: string;
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
    badge?: string;
    tools: Array<{ title: string; description: string }>;
}

function ToolCategory({ title, icon, iconBg, badge, tools }: ToolCategoryProps) {
    return (
        <div className="rounded-2xl border border-[#374151] bg-gradient-to-br from-[#2A2D35] to-[#363A42] p-6 shadow-lg md:p-8">
            <div className="mb-6 flex items-center gap-3">
                <div className={`h-10 w-10 ${iconBg} flex items-center justify-center rounded-lg`}>{icon}</div>
                <h3 className="text-xl font-bold text-[#F8FAFC]">{title}</h3>
                {badge && <span className="rounded-full bg-[#E81932]/10 px-3 py-1 text-xs font-semibold text-[#E81932]">{badge}</span>}
            </div>
            <div className="grid gap-4 md:grid-cols-2">
                {tools.map((tool, index) => (
                    <div key={index} className="rounded-lg border border-[#CBD5E1]/10 bg-[#363A42] p-4">
                        <h4 className="mb-2 font-semibold text-[#F8FAFC]">{tool.title}</h4>
                        <p className="text-sm text-[#CBD5E1]">{tool.description}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function FlightIcon() {
    return (
        <svg className="h-6 w-6 text-[#E81932]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
        </svg>
    );
}

function BookingIcon() {
    return (
        <svg className="h-6 w-6 text-[#60A5FA]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
        </svg>
    );
}

function TravelIcon() {
    return (
        <svg className="h-6 w-6 text-[#22C55E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
        </svg>
    );
}
